import { useState, useRef, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  format, 
  isSameDay, 
  addMonths, 
  subMonths, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameMonth, 
  isToday, 
  isBefore, 
  startOfToday,
  setHours,
  setMinutes
} from 'date-fns'
import { DollarSign, Clock, Check, ChevronLeft, ChevronRight, Undo2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Timestamp } from 'firebase/firestore'
import { useLongPress } from '@/hooks/useLongPress'
import { deleteMessageFromChat } from '@/lib/chatData'
import { useAuthState } from '@/hooks/useAuth'
import { useUserData } from '@/hooks/useUserData'
import { useArtists } from '@/hooks/useArtists'
import { useBookingSystem, type Booking } from '@/contexts/BookingSystemContext'
import { CREATE_BOOKING_ENDPOINT, CANCEL_BOOKING_ENDPOINT_PRODUCTION } from '@/config/endpoints'
import { useAnalytics } from '@/hooks/useAnalytics'
import { sendMetaStandardEvent } from '@/lib/metaEvents'

// Generate time slots: 9 AM to 9 PM in 30-minute increments
const generateTimeSlots = (): string[] => {
  const slots: string[] = []
  for (let hour = 9; hour < 21; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      slots.push(time)
    }
  }
  return slots
}

const TIME_SLOTS = generateTimeSlots()

interface SimpleAvailabilityMessageProps {
  priceRangeMin: number
  priceRangeMax: number
  availableDates: Date[] | Timestamp[]
  estimatedTime?: number | 'unknown'
  senderName?: string
  isOwnMessage: boolean // true if current user sent this message (artist view)
  messageId?: string // Message ID for deletion
  chatId?: string // Chat ID for deletion
  artistId?: string // The artist who sent this booking message
  onBookingCreated?: () => void // Callback when booking is successfully created
  bookingTime?: string // Optional time for confirmed bookings (format: HH:MM)
  isConfirmed?: boolean // Flag to indicate this is a booking confirmation
  availableTimeSlots?: Record<string, string[]> // Maps date (YYYY-MM-DD) to array of time strings (HH:MM)
}

export function SimpleAvailabilityMessage({
  priceRangeMin,
  priceRangeMax,
  availableDates: availableDatesProp,
  estimatedTime,
  senderName,
  isOwnMessage,
  messageId,
  chatId,
  artistId,
  onBookingCreated,
  bookingTime,
  isConfirmed,
  availableTimeSlots
}: SimpleAvailabilityMessageProps) {
  // Convert Firestore Timestamps to Date objects
  const availableDates = availableDatesProp.map(date => 
    date instanceof Timestamp ? date.toDate() : date
  )

  const { user } = useAuthState()
  const { userData } = useUserData()
  const { artists } = useArtists(artistId ? [artistId] : [])
  const { subscribeToBookingByMessageId } = useBookingSystem()
  const { trackEvent } = useAnalytics()

  const artistDisplayName = useMemo(() => {
    if (isOwnMessage) {
      return userData?.displayName || senderName || 'artist'
    }
    return artists[0]?.displayName || senderName || 'artist'
  }, [isOwnMessage, userData?.displayName, artists, senderName])

  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{ start: number; end: number; label: string } | null>(null)
  const [currentMonth, setCurrentMonth] = useState(availableDates[0] || new Date())
  const [slideDirection, setSlideDirection] = useState(0)
  const [bookingStep, setBookingStep] = useState<'calendar' | 'time'>('calendar')
  const [justSelectedDate, setJustSelectedDate] = useState(false)
  const [showDeleteOverlay, setShowDeleteOverlay] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [messagePosition, setMessagePosition] = useState({ top: 0, left: 0, width: 0, height: 0 })
  const [messageSnapshot, setMessageSnapshot] = useState<{ html: string; className: string } | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [existingBooking, setExistingBooking] = useState<Booking | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [lockedBooking, setLockedBooking] = useState<Booking | null>(null) // Locked state for completed bookings
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const messageRef = useRef<HTMLDivElement>(null)

  // Artist sent the message = they see greyed out view
  // User receives the message = they see interactive view
  const isArtistView = isOwnMessage
  
  // Use locked booking if available (completed bookings), otherwise use live subscription
  const activeBooking = lockedBooking || existingBooking

  // Update message position and capture snapshot when overlay is shown
  useEffect(() => {
    if (showDeleteOverlay && messageRef.current) {
      const rect = messageRef.current.getBoundingClientRect()
      setMessagePosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      })
      // Capture the message HTML and classes for rendering above blur
      setMessageSnapshot({
        html: messageRef.current.innerHTML,
        className: messageRef.current.className
      })
    }
  }, [showDeleteOverlay])

  // Subscribe to booking changes in real-time
  // Subscribe for both artists and clients to see booking status
  useEffect(() => {
    if (!chatId || !messageId) {
      return
    }
    
    console.log('[SimpleAvailabilityMessage] Setting up booking subscription for chatId:', chatId, 'view:', isArtistView ? 'artist' : 'client', 'messageId:', messageId, 'isConfirmed:', isConfirmed)
    
    // Subscribe to booking changes for THIS specific message
    const unsubscribe = subscribeToBookingByMessageId(chatId, messageId, (booking) => {
      console.log('[SimpleAvailabilityMessage] Booking update received:', {
        bookingExists: !!booking,
        bookingStatus: booking?.status,
        bookingMessageId: booking?.messageId,
        thisMessageId: messageId,
        isArtistView
      })
      
      // The subscription is already filtered by messageId in Firestore,
      // so any booking we receive here is guaranteed to be for this message
      if (booking) {
        // If this booking is completed or no_show, lock it to this message
        if (booking.status === 'completed' || booking.status === 'no_show') {
          console.log('[SimpleAvailabilityMessage] Locking completed/no-show booking to message:', messageId)
          setLockedBooking(booking)
          setExistingBooking(null) // Clear the regular state
        } else {
          console.log('[SimpleAvailabilityMessage] Setting active booking (status:', booking.status, ')')
          setExistingBooking(booking)
          setLockedBooking(null) // Clear locked state if booking becomes active again
        }
      } else {
        // No booking found for this message
        console.log('[SimpleAvailabilityMessage] No booking found for this message')
        setExistingBooking(null)
        setLockedBooking(null)
      }
    })
    
    return () => {
      console.log('[SimpleAvailabilityMessage] Cleaning up booking subscription')
      unsubscribe()
    }
  }, [chatId, subscribeToBookingByMessageId, messageId, isArtistView, isConfirmed])

  const handleDateClick = (date: Date) => {
    if (isArtistView || activeBooking) return
    // Only allow selection of available dates
    const isAvailable = availableDates.some(d => isSameDay(d, date))
    if (isAvailable) {
      const isSelecting = !isSameDay(date, selectedDate || new Date('1970-01-01'))
      setSelectedDate(isSelecting ? date : null)
      setSelectedTimeSlot(null) // Reset time slot when changing date
      if (isSelecting) {
        setJustSelectedDate(true) // Trigger the highlight animation
        setBookingStep('time') // Move to time selection
      } else {
        setBookingStep('calendar')
      }
    }
  }

  const handleTimeSlotClick = (slot: { start: number; end: number; label: string }) => {
    setSelectedTimeSlot(
      selectedTimeSlot?.start === slot.start && selectedTimeSlot?.end === slot.end 
        ? null 
        : slot
    )
  }

  const handleBackToCalendar = () => {
    setBookingStep('calendar')
    setJustSelectedDate(false)
  }

  // Generate 30-minute time slots from 9 AM to 9 PM
  const get30MinuteIntervals = (): { start: number; end: number; label: string }[] => {
    // If availableTimeSlots are provided for the selected date, use those
    if (selectedDate && availableTimeSlots) {
      const dateKey = format(selectedDate, 'yyyy-MM-dd')
      const timesForDate = availableTimeSlots[dateKey]
      
      if (timesForDate && timesForDate.length > 0) {
        return timesForDate.map(timeSlot => {
          const [hour, minute] = timeSlot.split(':').map(Number)
          const startMinutes = hour * 60 + minute
          return {
            start: startMinutes,
            end: startMinutes + 30,
            label: timeSlot
          }
        })
      }
    }
    
    // Otherwise, use all time slots from 9 AM to 9 PM
    return TIME_SLOTS.map(timeSlot => {
      const [hour, minute] = timeSlot.split(':').map(Number)
      const startMinutes = hour * 60 + minute
      return {
        start: startMinutes,
        end: startMinutes + 30,
        label: timeSlot
      }
    })
  }

  const timeSlotIntervals = get30MinuteIntervals()

  // Clear the highlight after brief delay
  useEffect(() => {
    if (justSelectedDate) {
      const timer = setTimeout(() => {
        setJustSelectedDate(false)
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [justSelectedDate])

  const handlePrevMonth = () => {
    setSlideDirection(-1)
    setCurrentMonth(prev => subMonths(prev, 1))
  }

  const handleNextMonth = () => {
    setSlideDirection(1)
    setCurrentMonth(prev => addMonths(prev, 1))
  }

  const isDateAvailable = (date: Date): boolean => {
    return availableDates.some(d => isSameDay(d, date))
  }

  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd })

  // Get day names for the header (Mon-Sun)
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

  // Calculate padding days for the start of the month
  const startPadding = monthStart.getDay() === 0 ? 6 : monthStart.getDay() - 1
  const paddingDays = Array.from({ length: startPadding }, () => null)

  // Animation variants
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction * 50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction * -50,
      opacity: 0
    })
  }

  const handleBookDate = async () => {
    if (!selectedDate || !selectedTimeSlot || isArtistView || !user || !chatId || !artistId) return

    console.log('[SimpleAvailabilityMessage:createBooking] 🎫 Starting booking creation flow')
    console.log('[SimpleAvailabilityMessage:createBooking] Selection details:', {
      selectedDate: selectedDate.toISOString(),
      selectedTimeSlot: selectedTimeSlot,
      user: user.email,
      artistId: artistId,
      chatId: chatId,
      priceRangeMin,
      priceRangeMax
    })

    setIsSubmitting(true)
    setError(null)

    try {
      // Create timestamp from selected date and time slot
      const hours = Math.floor(selectedTimeSlot.start / 60)
      const minutes = selectedTimeSlot.start % 60
      const bookingDateTime = setMinutes(setHours(selectedDate, hours), minutes)
      const bookingTimestamp = bookingDateTime.getTime() // Unix timestamp in milliseconds

      console.log('[SimpleAvailabilityMessage:createBooking] Calculated booking date/time:', {
        hours,
        minutes,
        bookingDateTime: bookingDateTime.toISOString(),
        bookingTimestamp
      })

      // Prepare request JSON
      const finalEstimatedTime = estimatedTime === 'unknown' ? 1 : (estimatedTime || 1)
      console.log('[SimpleAvailabilityMessage:createBooking] Estimated time to send to backend:', finalEstimatedTime, `(originally: ${estimatedTime})`)
      
      const clientData: Record<string, string | boolean> = {}
      
      // Add auto-populated fields
      if (user?.email) clientData.email = user.email
      if (userData?.instagram) clientData.username = userData.instagram
      
      // Determine if this is fixed or range pricing
      // If min and max are equal, it's a fixed price
      const isPriceFixed = priceRangeMin === priceRangeMax
      const priceMode = isPriceFixed ? 'fixed' : 'range'
      
      const requestData = {
        chatId: chatId,
        clientData: clientData,
        timestamp: bookingTimestamp,
        bookingDateTime: bookingDateTime.toISOString(),
        artistId: artistId,
        estimatedTime: finalEstimatedTime,
        priceMode: priceMode,
        priceRangeMin: priceRangeMin,
        priceRangeMax: priceRangeMax,
        messageId: messageId // Link booking to this specific availability message
      }

      console.log('[SimpleAvailabilityMessage:createBooking] Booking request payload:', requestData)

      // Call the create_booking endpoint
      console.log('[SimpleAvailabilityMessage:createBooking] Sending booking creation request to backend')
      const response = await fetch(CREATE_BOOKING_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      })

      console.log('[SimpleAvailabilityMessage:createBooking] Backend response:', {
        status: response.status,
        statusText: response.statusText
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('[SimpleAvailabilityMessage:createBooking] ❌ Backend error:', errorData)
        throw new Error(errorData.message || `Failed to create booking: ${response.statusText}`)
      }

      const result = await response.json()
      console.log('[SimpleAvailabilityMessage:createBooking] ✅ Booking created in backend')
      console.log('[SimpleAvailabilityMessage:createBooking] Backend response:', result)

      await trackEvent('chat_availability_accepted', {
        chat_id: chatId,
        artist_id: artistId,
        mode: 'simple',
        booking_date: bookingDateTime.toISOString().split('T')[0],
        booking_time: bookingDateTime.toISOString(),
        booking_timestamp: bookingTimestamp,
        price_min: priceRangeMin,
        price_max: priceRangeMax,
        estimated_time: estimatedTime
      })

      await sendMetaStandardEvent('Purchase', {
        value: Math.round((priceRangeMin + priceRangeMax) / 2),
        currency: 'EUR',
        content_name: 'tattoo_booking',
        content_category: 'tattoo',
        num_items: 1,
      })
      
      // The subscription will automatically update existingBooking state
      // But we notify parent component to trigger any additional updates (e.g., header banner)
      if (onBookingCreated) {
        console.log('[SimpleAvailabilityMessage:createBooking] Calling onBookingCreated callback')
        onBookingCreated()
      }
      
      console.log('[SimpleAvailabilityMessage:createBooking] ✅ Booking creation flow complete!')
    } catch (err) {
      console.error('[SimpleAvailabilityMessage:createBooking] ❌ Error creating booking:', err)
      console.error('[SimpleAvailabilityMessage:createBooking] Error details:', {
        name: err instanceof Error ? err.name : 'Unknown',
        message: err instanceof Error ? err.message : String(err)
      })
      setError(err instanceof Error ? err.message : 'Failed to create booking. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Show confirmation dialog
  const handleShowCancelConfirm = () => {
    setShowCancelConfirm(true)
  }

  // Cancel confirmation
  const handleConfirmCancel = async () => {
    if (!activeBooking) {
      console.error('[SimpleAvailabilityMessage:cancelBooking] No active booking to cancel')
      return
    }

    console.log('[SimpleAvailabilityMessage:cancelBooking] 🔴 Starting booking cancellation flow')
    console.log('[SimpleAvailabilityMessage:cancelBooking] Booking to cancel:', {
      bookingId: activeBooking.id,
      clientUsername: activeBooking.clientUsername,
      chatId: activeBooking.chatId,
      isArtistView
    })

    setIsCancelling(true)
    setError(null)
    try {
      console.log('[SimpleAvailabilityMessage:cancelBooking] Calling backend to cancel booking at:', CANCEL_BOOKING_ENDPOINT_PRODUCTION)
      const response = await fetch(CANCEL_BOOKING_ENDPOINT_PRODUCTION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookingId: activeBooking.id
        })
      })

      console.log('[SimpleAvailabilityMessage:cancelBooking] Backend response:', {
        status: response.status,
        statusText: response.statusText
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('[SimpleAvailabilityMessage:cancelBooking] ❌ Backend error:', errorData)
        throw new Error(errorData.message || `Failed to cancel booking: ${response.statusText}`)
      }

      console.log('[SimpleAvailabilityMessage:cancelBooking] ✅ Booking cancelled')
      
      // Clear local booking state immediately (subscription will also update it)
      setExistingBooking(null)
      setLockedBooking(null)
      setShowCancelConfirm(false)
      
      // Notify parent component to refetch booking (for header banner)
      if (onBookingCreated) {
        console.log('[SimpleAvailabilityMessage:cancelBooking] Calling onBookingCreated callback')
        onBookingCreated()
      }
      
      console.log('[SimpleAvailabilityMessage:cancelBooking] ✅ Booking cancellation complete!')
    } catch (err) {
      console.error('[SimpleAvailabilityMessage:cancelBooking] ❌ Error cancelling booking:', err)
      console.error('[SimpleAvailabilityMessage:cancelBooking] Error details:', {
        name: err instanceof Error ? err.name : 'Unknown',
        message: err instanceof Error ? err.message : String(err)
      })
      const errorMsg = err instanceof Error ? err.message : 'Failed to cancel booking. Please try again.'
      console.error('[SimpleAvailabilityMessage:cancelBooking] Setting error:', errorMsg)
      setError(errorMsg)
      
      // Re-enable cancelling state so user can see the error and try again
      setIsCancelling(false)
    }
  }

  // Cancel confirmation dialog
  const handleCancelConfirmDialog = () => {
    setShowCancelConfirm(false)
  }

  // Check if this booking is for May 9th, 2026
  const isTime4TatsBooking = useMemo(() => {
    if (activeBooking?.scheduledTimestamp) {
      const bookingDate = activeBooking.scheduledTimestamp.toDate()
      return bookingDate.getFullYear() === 2026 && 
             bookingDate.getMonth() === 4 && 
             bookingDate.getDate() === 9
    }
    return false
  }, [activeBooking])

  const handleLongPress = () => {
    // Allow deletion for clients and artists if no active booking
    if (messageId && chatId && !activeBooking) {
      setShowDeleteOverlay(true)
    }
  }

  const handleDelete = async () => {
    if (!messageId || !chatId) return
    
    setIsDeleting(true)
    try {
      await deleteMessageFromChat(chatId, messageId)
      // Message will be removed automatically via Firestore listener
    } catch (error) {
      console.error('Failed to delete message:', error)
      setIsDeleting(false)
      setShowDeleteOverlay(false)
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteOverlay(false)
  }

  // Long press handlers
  const longPressHandlers = useLongPress({
    onLongPress: handleLongPress,
    ms: 500
  })

  return (
    <>
      <div className="w-full mb-4 flex justify-center relative">
        <motion.div 
          id={messageId ? `simple-availability-message-${messageId}` : undefined}
          ref={messageRef}
          {...longPressHandlers}
          className={cn(
            "rounded-[18px] p-4 shadow-sm w-full max-w-[min(100%,520px)]",
            isTime4TatsBooking ? "time4tats-btn" : "bg-primary text-white"
          )}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ 
            opacity: showDeleteOverlay ? 0 : 1, 
            scale: 1,
            y: 0
          }}
          style={{
            position: 'static',
            zIndex: 'auto'
          }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
        >
        {/* Completed Booking View */}
        {activeBooking?.status === 'completed' ? (
          <>
            {/* Success Icon and Message */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-black/30">
                <Check className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-base text-white">
                  Tattoo Done!
                </h4>
                <p className="text-sm text-white/80">
                  {isTime4TatsBooking 
                    ? 'time4tats, May 9, 2026'
                    : format(activeBooking.scheduledTimestamp.toDate(), 'EEEE, MMMM d, yyyy')}
                </p>
              </div>
            </div>

            {/* Booking Details */}
            <div className="space-y-2 text-sm border-t pt-3 border-white/20 text-white/90">
              <div className="flex justify-between items-center gap-2">
                <span className="text-white/70 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-white/70" />
                  Scheduled Time:
                </span>
                <span className="font-medium">
                  {format(activeBooking.scheduledTimestamp.toDate(), 'HH:mm')}
                </span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <span className="text-white/70 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-white/70" />
                  Estimated Duration:
                </span>
                <span className="font-medium">{estimatedTime === 'unknown' ? 'Unknown' : `${estimatedTime}h`}</span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <span className="text-white/70 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-white/70" />
                  {priceRangeMin === priceRangeMax ? 'Price:' : 'Price Estimate:'}
                </span>
                <span className="font-medium">
                  {activeBooking?.bookingCompleteForm?.finalPrice 
                    ? `€${activeBooking.bookingCompleteForm.finalPrice}`
                    : priceRangeMin === 0 && priceRangeMax === 0 
                    ? 'To be discussed' 
                    : priceRangeMin === priceRangeMax
                    ? `€${priceRangeMin}`
                    : priceRangeMin === 0
                    ? `Up to €${priceRangeMax}`
                    : priceRangeMax === 0
                    ? `From €${priceRangeMin}`
                    : `€${priceRangeMin} - €${priceRangeMax}`
                  }
                </span>
              </div>
            </div>
          </>
        ) : activeBooking?.status === 'no_show' ? (
          <>
            {/* No Show Icon and Message */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-red-500/20">
                <X className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h4 className="font-semibold text-base text-white">
                  Booking - No Show
                </h4>
                <p className="text-sm text-white/80">
                  {format(activeBooking.scheduledTimestamp.toDate(), 'EEEE, MMMM d, yyyy')}
                </p>
              </div>
            </div>

            {/* No Show Message */}
            <div className="mb-4 p-3 rounded-md bg-white/10">
              <p className="text-xs text-white/80">
                {isArtistView 
                  ? "This booking was marked as no-show."
                  : "This booking was missed. Please reach out to reschedule."}
              </p>
            </div>

            {/* Booking Details */}
            <div className="space-y-2 text-sm border-t pt-3 border-white/20 text-white/90">
              <div className="flex justify-between items-center gap-2">
                <span className="text-white/70 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-white/70" />
                  Scheduled Time:
                </span>
                <span className="font-medium">
                  {format(activeBooking.scheduledTimestamp.toDate(), 'HH:mm')}
                </span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <span className="text-white/70 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-white/70" />
                  Estimated Duration:
                </span>
                <span className="font-medium">{estimatedTime === 'unknown' ? 'Unknown' : `${estimatedTime}h`}</span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <span className="text-white/70 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-white/70" />
                  {priceRangeMin === priceRangeMax ? 'Price:' : 'Price Estimate:'}
                </span>
                <span className="font-medium">
                  {priceRangeMin === 0 && priceRangeMax === 0 
                    ? 'To be discussed' 
                    : priceRangeMin === priceRangeMax
                    ? `€${priceRangeMin}`
                    : priceRangeMin === 0
                    ? `Up to €${priceRangeMax}`
                    : priceRangeMax === 0
                    ? `From €${priceRangeMin}`
                    : `€${priceRangeMin} - €${priceRangeMax}`
                  }
                </span>
              </div>
            </div>
          </>
        ) : activeBooking || isConfirmed ? (
          <>
            {/* Success Icon and Message */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-black/30">
                <Check className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-base text-white">
                  Booking Confirmed
                </h4>
                <p className="text-sm text-white/80">
                  {isTime4TatsBooking
                    ? 'time4tats, May 9, 2026'
                    : (activeBooking 
                      ? format(activeBooking.scheduledTimestamp.toDate(), 'EEEE, MMMM d, yyyy')
                      : availableDates[0]
                      ? format(availableDates[0], 'EEEE, MMMM d, yyyy')
                      : 'Booking confirmed'
                    )
                  }
                </p>
              </div>
            </div>

            {/* Booking Details */}
            <div className="space-y-2 text-sm border-t pt-3 border-white/20 text-white/90">
              <div className="flex justify-between items-center gap-2">
                <span className="text-white/70 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-white/70" />
                  Scheduled Time:
                </span>
                <span className="font-medium">
                  {activeBooking 
                    ? format(activeBooking.scheduledTimestamp.toDate(), 'HH:mm')
                    : bookingTime || 'TBD'
                  }
                </span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <span className="text-white/70 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-white/70" />
                  Estimated Duration:
                </span>
                <span className="font-medium">{estimatedTime === 'unknown' ? 'Unknown' : `${estimatedTime}h`}</span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <span className="text-white/70 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-white/70" />
                  {priceRangeMin === priceRangeMax ? 'Price:' : 'Price Estimate:'}
                </span>
                <span className="font-medium">
                  {priceRangeMin === 0 && priceRangeMax === 0 
                    ? 'To be discussed' 
                    : priceRangeMin === priceRangeMax
                    ? `€${priceRangeMin}`
                    : priceRangeMin === 0
                    ? `Up to €${priceRangeMax}`
                    : priceRangeMax === 0
                    ? `From €${priceRangeMin}`
                    : `€${priceRangeMin} - €${priceRangeMax}`
                  }
                </span>
              </div>
            </div>

            {/* Cancel Confirmation Dialog or Button */}
            {showCancelConfirm ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="mt-3 p-3 rounded-md bg-white/10 border border-white/20"
              >
                <p className="text-sm text-white mb-3 text-center">
                  Are you sure you want to cancel the booking?
                </p>
                <div className="flex gap-2 justify-center">
                  <motion.button
                    onClick={handleConfirmCancel}
                    disabled={isCancelling}
                    whileHover={{ scale: isCancelling ? 1 : 1.05 }}
                    whileTap={{ scale: isCancelling ? 1 : 0.95 }}
                    className="py-1.5 px-3 rounded-md text-xs font-medium bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                    type="button"
                  >
                    {isCancelling ? (
                      <>
                        <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      </>
                    ) : (
                      'Cancel'
                    )}
                  </motion.button>
                  <motion.button
                    onClick={handleCancelConfirmDialog}
                    disabled={isCancelling}
                    whileHover={{ scale: isCancelling ? 1 : 1.05 }}
                    whileTap={{ scale: isCancelling ? 1 : 0.95 }}
                    className="py-1.5 px-3 rounded-md text-xs font-medium bg-white/20 hover:bg-white/30 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    type="button"
                  >
                    Keep
                  </motion.button>
                </div>
              </motion.div>
            ) : isArtistView ? (
              <motion.button
                onClick={handleShowCancelConfirm}
                disabled={isCancelling}
                whileHover={{ scale: isCancelling ? 1 : 1.05 }}
                whileTap={{ scale: isCancelling ? 1 : 0.95 }}
                className="mt-3 py-1.5 px-3 rounded-md text-xs font-medium bg-black/30 hover:bg-black/40 text-white/80 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                type="button"
              >
                Cancel Booking
              </motion.button>
            ) : null}
          </>
        ) : (
          <>
            {/* Header */}
            <div className="mb-4">
              <h3 className="text-base font-semibold mb-3 text-white">
                {isArtistView ? 'Available Times Sent' : `Book your time with ${artistDisplayName}`}
              </h3>
              
              {/* Info Cards */}
              <div className="space-y-2 text-sm text-white">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-white/70" />
                  <span className="text-white/70">
                    {priceRangeMin === priceRangeMax ? 'Price:' : 'Price range:'}
                  </span>
                  <span className="font-medium">
                    {priceRangeMin === 0 && priceRangeMax === 0 
                      ? 'To be discussed' 
                      : priceRangeMin === priceRangeMax
                      ? `€${priceRangeMin}`
                      : priceRangeMin === 0
                      ? `Up to €${priceRangeMax}`
                      : priceRangeMax === 0
                      ? `From €${priceRangeMin}`
                      : `€${priceRangeMin} - €${priceRangeMax}`
                    }
                  </span>
                </div>
                {estimatedTime && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-white/70" />
                    <span className="text-white/70">Estimated duration:</span>
                    <span className="font-medium">
                      {estimatedTime === 'unknown' ? 'To be determined' : `${estimatedTime}h`}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Calendar for Available Dates */}
            {!isArtistView && (
              <div className="bg-card rounded-lg p-3 mb-3">
                <AnimatePresence mode="wait">
                  {/* Step 1: Calendar - show when on calendar step */}
                  {bookingStep === 'calendar' && (
                    <motion.div
                      key="calendar"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    >
                      {/* Calendar Header */}
                      <div className="flex items-center justify-between mb-3">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={handlePrevMonth}
                          className="p-1 rounded-full hover:bg-muted"
                          type="button"
                        >
                          <ChevronLeft className="w-4 h-4 text-foreground" />
                        </motion.button>
                        
                        <motion.h2 
                          key={currentMonth.toISOString()}
                          custom={slideDirection}
                          variants={slideVariants}
                          initial="enter"
                          animate="center"
                          exit="exit"
                          transition={{ type: "spring", stiffness: 300, damping: 30 }}
                          className="text-sm font-medium text-foreground"
                        >
                          {format(currentMonth, 'MMMM yyyy')}
                        </motion.h2>
                        
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={handleNextMonth}
                          className="p-1 rounded-full hover:bg-muted"
                          type="button"
                        >
                          <ChevronRight className="w-4 h-4 text-foreground" />
                        </motion.button>
                      </div>

                      {/* Calendar Grid */}
                      <div className="grid grid-cols-7 gap-1">
                        {/* Day names */}
                        {dayNames.map(day => (
                          <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
                            {day}
                          </div>
                        ))}
                        
                        {/* Padding days */}
                        {paddingDays.map((_, i) => (
                          <div key={`pad-${i}`} className="aspect-square" />
                        ))}
                        
                        {/* Actual days */}
                        {daysInMonth.map(date => {
                          const isSelected = selectedDate && isSameDay(date, selectedDate)
                          const isPast = isBefore(date, startOfToday())
                          const isAvailable = isDateAvailable(date)
                          const isDisabled = isPast || !isAvailable
                          
                          return (
                            <motion.button
                              key={date.toISOString()}
                              whileHover={{ scale: isDisabled ? 1 : 0.95 }}
                              whileTap={{ scale: isDisabled ? 1 : 0.9 }}
                              onClick={() => !isDisabled && handleDateClick(date)}
                              disabled={isDisabled}
                              type="button"
                              className={cn(
                                "aspect-square rounded-md flex items-center justify-center text-xs transition-colors relative",
                                isSelected && "bg-primary text-primary-foreground",
                                !isSelected && !isDisabled && "hover:bg-muted",
                                isToday(date) && !isSelected && !isDisabled && "border border-primary",
                                !isSameMonth(date, currentMonth) && "text-muted-foreground opacity-50",
                                isDisabled && "opacity-30 cursor-not-allowed",
                                !isSelected && isAvailable && "text-foreground font-medium",
                                !isSelected && !isAvailable && "text-foreground"
                              )}
                            >
                              {format(date, 'd')}
                            </motion.button>
                          )
                        })}
                      </div>
                    </motion.div>
                  )}

                  {/* Step 2: Time Slots - show when on time step */}
                  {bookingStep === 'time' && selectedDate && !justSelectedDate && (
                    <motion.div 
                      key="timeslots"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      className="min-h-[320px] flex flex-col"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-foreground">
                          {`Available Times - ${format(selectedDate, 'MMM d')}`}
                        </h4>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={handleBackToCalendar}
                          className="p-1 rounded-full hover:bg-muted text-foreground"
                          type="button"
                          title="Back to calendar"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </motion.button>
                      </div>

                      <div className="space-y-2 max-h-[200px] overflow-y-auto mb-3">
                        {timeSlotIntervals.map((slot, index) => {
                          const isSelected = selectedTimeSlot?.start === slot.start && selectedTimeSlot?.end === slot.end
                          return (
                            <motion.button
                              key={index}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.05 }}
                              onClick={() => handleTimeSlotClick(slot)}
                              className={cn(
                                "w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors",
                                isSelected 
                                  ? "bg-primary text-primary-foreground" 
                                  : "bg-muted hover:bg-muted/80 text-foreground"
                              )}
                              type="button"
                            >
                              <span className="font-medium">
                                {slot.label}
                              </span>
                              <Clock className="w-4 h-4" />
                            </motion.button>
                          )
                        })}
                      </div>

                      {/* Error Message */}
                      {error && (
                        <div className="mb-3 p-3 bg-destructive/10 border border-destructive/50 rounded-md">
                          <p className="text-xs text-destructive">{error}</p>
                        </div>
                      )}

                      <div className="flex-1"></div>

                      {/* Book Button - always at bottom */}
                      <motion.button
                        onClick={handleBookDate}
                        disabled={!selectedDate || !selectedTimeSlot || isSubmitting}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileHover={{ scale: !selectedDate || !selectedTimeSlot || isSubmitting ? 1 : 1.02 }}
                        whileTap={{ scale: !selectedDate || !selectedTimeSlot || isSubmitting ? 1 : 0.98 }}
                        className={cn(
                          "w-full py-3 px-4 rounded-md text-sm font-semibold transition-colors",
                          !selectedDate || !selectedTimeSlot || isSubmitting
                            ? "bg-background text-muted-foreground cursor-not-allowed"
                            : "bg-primary text-primary-foreground hover:opacity-90"
                        )}
                        type="button"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Booking...
                          </span>
                        ) : (
                          'Book Time'
                        )}
                      </motion.button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Artist View: Show available dates as calendar */}
            {isArtistView && (
              <div className="bg-card rounded-lg p-3 mb-3">
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-3">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handlePrevMonth}
                    className="p-1 rounded-full hover:bg-muted"
                    type="button"
                  >
                    <ChevronLeft className="w-4 h-4 text-foreground" />
                  </motion.button>
                  
                  <motion.h2 
                    key={currentMonth.toISOString()}
                    custom={slideDirection}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className="text-sm font-medium text-foreground"
                  >
                    {format(currentMonth, 'MMMM yyyy')}
                  </motion.h2>
                  
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleNextMonth}
                    className="p-1 rounded-full hover:bg-muted"
                    type="button"
                  >
                    <ChevronRight className="w-4 h-4 text-foreground" />
                  </motion.button>
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {/* Day names */}
                  {dayNames.map(day => (
                    <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
                      {day}
                    </div>
                  ))}
                  
                  {/* Padding days */}
                  {paddingDays.map((_, i) => (
                    <div key={`pad-${i}`} className="aspect-square" />
                  ))}
                  
                  {/* Actual days */}
                  {daysInMonth.map(date => {
                    const isPast = isBefore(date, startOfToday())
                    const isAvailable = isDateAvailable(date)
                    const isDisabled = isPast || !isAvailable
                    
                    return (
                      <div
                        key={date.toISOString()}
                        className={cn(
                          "aspect-square rounded-md flex items-center justify-center text-xs transition-colors relative",
                          isAvailable && "bg-primary text-primary-foreground font-medium",
                          isToday(date) && isAvailable && "border-2 border-white",
                          !isSameMonth(date, currentMonth) && "text-muted-foreground opacity-50",
                          isDisabled && "opacity-30 text-muted-foreground",
                        )}
                      >
                        {format(date, 'd')}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Artist View: Info */}
            {isArtistView && (
              <div className="text-center text-xs text-white/90">
                Waiting for the client to book a time
              </div>
            )}
          </>
        )}
        </motion.div>
      </div>

      {/* Instagram-style Blur Overlay with Delete Option - Using Portal */}
      {showDeleteOverlay && createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50"
            style={{ 
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              margin: 0
            }}
          >
            {/* Blur backdrop - covers entire viewport including header and input */}
            <div
              onClick={handleCancelDelete}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            
            {/* Clone of the message above the blur - unblurred */}
            {messageSnapshot && (
              <motion.div
                initial={{ scale: 1 }}
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ 
                  duration: 0.3,
                  times: [0, 0.5, 1],
                  ease: "easeInOut"
                }}
                className={messageSnapshot.className}
                style={{
                  position: 'absolute',
                  top: messagePosition.top,
                  left: messagePosition.left,
                  width: messagePosition.width,
                  height: messagePosition.height,
                  zIndex: 60,
                  pointerEvents: 'none'
                }}
                dangerouslySetInnerHTML={{ __html: messageSnapshot.html }}
              />
            )}
            
            {/* Unsend button positioned below the message */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className="absolute flex gap-3"
              style={{
                top: `${messagePosition.top + messagePosition.height + 16}px`,
                right: `${window.innerWidth - (messagePosition.left + messagePosition.width)}px`,
              }}
            >
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className={cn(
                  "flex items-center gap-2 px-6 py-3 rounded-[18px]",
                  "bg-background text-red-500 font-semibold text-sm",
                  "hover:bg-muted active:scale-95",
                  "transition-all shadow-lg",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                <Undo2 className="w-5 h-5" />
                <span>{isDeleting ? 'Unsending...' : 'Unsend'}</span>
              </button>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </>
  )
}
